import React, { forwardRef } from 'react';

import { mergeRefs } from 'use-callback-ref';

import Badge from '@atlaskit/badge/badge';
import Button, { type ButtonProps } from '@atlaskit/button/default/button';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { UiViewedEvent } from '../analytics/ui-viewed-event';
import type { ButtonContent } from '../services/use-button-content';

import { getIconElement } from './get-icon-element';

interface EditionAwarenessButtonAdsProps extends Pick<ButtonProps, 'onClick'> {
	analyticsAttributes: Record<string, unknown>;
	buttonContent: ButtonContent;
	onMouseEnter?: () => void;
}

export const EditionAwarenessButtonAds: React.ForwardRefExoticComponent<
	EditionAwarenessButtonAdsProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, EditionAwarenessButtonAdsProps>(
	function EditionAwarenessButtonAds(
		{ buttonContent, onClick, analyticsAttributes, onMouseEnter },
		ref,
	) {
		const { text, appearance = 'default', tooltip, icon, badge } = buttonContent;

		const iconElement = getIconElement(icon);
		const iconBefore = iconElement ? () => iconElement : undefined;
		const iconAfter = badge ? () => <Badge appearance="discovery">{badge}</Badge> : undefined;

		return (
			<Tooltip content={tooltip}>
				{(tooltipProps) => {
					const { ref: tooltipRef, ...restTooltipProps } = tooltipProps;

					return (
						<Button
							{...restTooltipProps}
							ref={mergeRefs([tooltipRef, ref])}
							appearance={appearance as ButtonProps['appearance']}
							onClick={onClick}
							onMouseEnter={onMouseEnter}
							iconBefore={iconBefore}
							iconAfter={iconAfter}
						>
							{text}
							<UiViewedEvent
								actionSubjectId="editionAwareness"
								actionSubject="button"
								attributes={analyticsAttributes}
							/>
						</Button>
					);
				}}
			</Tooltip>
		);
	},
);
